import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HiTrash } from 'react-icons/hi2';
import type { RootState } from '@/store/store';
import { useGetBillingStatusQuery, useRemoveDemoDataMutation } from '@/store/billingApi';
import { isApiError, getErrorMessage } from '@/utils/errorUtils';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import { Section, SectionTitle } from '@/components/layout/Toolbar.styled';
import { SurfaceCard, LoadingCenter } from '@/styles/shared.styled';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import {
  StatusGrid,
  TrialBadge,
  EmptyText,
  DangerCard,
  DangerDescription,
  DangerButton,
  FeedbackMessage,
} from './SettingsPage.styled';

function trialDaysRemaining(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt).getTime();
  if (Number.isNaN(end)) return null;
  const diffMs = end - Date.now();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const isAdmin = useSelector(
    (state: RootState) => state.auth.user?.permissions?.includes('admin') ?? false,
  );

  const { data: status, isLoading } = useGetBillingStatusQuery();
  const [removeDemoData, { isLoading: isRemoving }] = useRemoveDemoDataMutation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedback, setFeedback] = useState<{ variant: 'success' | 'error'; message: string } | null>(null);

  const daysLeft = trialDaysRemaining(status?.trialEndsAt ?? null);

  const handleRemove = async () => {
    setConfirmOpen(false);
    setFeedback(null);
    const result = await removeDemoData();
    if (isApiError(result)) {
      setFeedback({ variant: 'error', message: getErrorMessage(result.error) });
    } else {
      setFeedback({ variant: 'success', message: t('settings.demoData.success') });
    }
  };

  return (
    <PageContent>
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />

      <Section>
        <SurfaceCard>
          <SectionTitle>{t('settings.subscription.title')}</SectionTitle>
          {isLoading ? (
            <LoadingCenter>
              <LoadingSpinner />
            </LoadingCenter>
          ) : status && (status.plan || status.status) ? (
            <StatusGrid>
              <dt>{t('settings.subscription.plan')}</dt>
              <dd>{status.plan ? t(`signup.plan${capitalize(status.plan)}`, status.plan) : '—'}</dd>
              <dt>{t('settings.subscription.status')}</dt>
              <dd>
                {status.status
                  ? t(`settings.subscription.statuses.${status.status}`, status.status)
                  : '—'}
              </dd>
              {daysLeft !== null && (
                <>
                  <dt>{t('settings.subscription.trial')}</dt>
                  <dd>
                    <TrialBadge>{t('settings.subscription.daysRemaining', { count: daysLeft })}</TrialBadge>
                  </dd>
                </>
              )}
            </StatusGrid>
          ) : (
            <EmptyText>{t('settings.subscription.none')}</EmptyText>
          )}
        </SurfaceCard>
      </Section>

      {isAdmin && (
        <Section>
          <DangerCard>
            <SectionTitle>{t('settings.demoData.title')}</SectionTitle>
            <DangerDescription>{t('settings.demoData.description')}</DangerDescription>
            <DangerButton type="button" disabled={isRemoving} onClick={() => setConfirmOpen(true)}>
              <HiTrash />
              {t('settings.demoData.button')}
            </DangerButton>
            {feedback && (
              <FeedbackMessage $variant={feedback.variant}>{feedback.message}</FeedbackMessage>
            )}
          </DangerCard>
        </Section>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={t('settings.demoData.confirmTitle')}
        message={t('settings.demoData.confirmMessage')}
        variant="danger"
        confirmLabel={t('confirm.delete')}
        onConfirm={handleRemove}
        onCancel={() => setConfirmOpen(false)}
      />
    </PageContent>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
